/**
 * useCategories hook
 * Fetches category list once and caches at module level.
 * Returns { byCode: { CODE: { name, color } }, list: [...] }
 */
import { useState, useEffect } from 'react';
import ApiService from '../services/ApiService';

// Module-level cache
let _cache = null;
let _promise = null;

const DEFAULT_COLORS = {
  BUSINESS_SKILLS:  '#8B4513',
  GENAI:            '#000080',
  INDUSTRY:         '#808080',
  METHODOLOGIES:    '#FF8C00',
  PRODUCT_PORTFOLIO:'#00CED1',
  SOFT_SKILLS:      '#0066CC',
  STRATEGY:         '#800080',
  SUSTAINABILITY:   '#2E8B57',
  TECHNOLOGY_TRENDS:'#008080',
  TOOLS:            '#FFD700',
};

const FALLBACK = {
  byCode: Object.fromEntries(
    Object.entries(DEFAULT_COLORS).map(([k, v]) => [k, { name: k, color: v }])
  ),
  list: [],
};

async function fetchCategories() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = ApiService.getCategories()
    .then(res => {
      // ApiService.getCategories() returns the raw array directly
      const rawList = Array.isArray(res) ? res : (res.data || []);
      const list = rawList.map(c => ({
        code:  c.code,
        name:  c.display_name || c.displayName || c.code,
        color: c.color || DEFAULT_COLORS[c.code] || '#005f99',
      }));
      const byCode = {};
      list.forEach(c => { byCode[c.code] = { name: c.name, color: c.color }; });
      _cache = { byCode, list };
      return _cache;
    })
    .catch(() => FALLBACK);
  return _promise;
}

export function invalidateCategoryCache() {
  _cache = null;
  _promise = null;
}

export function useCategories() {
  const [data, setData] = useState(_cache || FALLBACK);

  useEffect(() => {
    if (!_cache) {
      fetchCategories().then(setData);
    }
  }, []);

  return data;
}

export default useCategories;
